import React, { useEffect, useMemo, useState } from 'react';
import { config } from '../config';
import { Icons } from './Icons';
import './ProtestMonitorConfigModal.css';

export const PROTEST_TARGET_LOCATION_MAX_LENGTH = 200;
export const PROTEST_RECIPIENT_MAX_COUNT = 20;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyTarget = () => ({
  location_scope: 'city',
  name: '',
  region: config.protestReportRegions[0] || 'US',
});

export function moveProtestTarget(targets, index, direction) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= targets.length) return targets;
  const next = [...targets];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

export function moveProtestRecipient(recipients, index, direction) {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= recipients.length) return recipients;
  const next = [...recipients];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

export function validateProtestTargets(targets) {
  if (!Array.isArray(targets) || targets.length < 1) {
    return 'Add at least one target.';
  }
  if (targets.length > 50) {
    return 'A maximum of 50 targets is allowed.';
  }

  const seen = new Set();
  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];
    const name = (target.name || '').trim();
    if (!name) return `Target ${index + 1} needs a location.`;
    if (name.length > PROTEST_TARGET_LOCATION_MAX_LENGTH) {
      return `Target ${index + 1} location must be ${PROTEST_TARGET_LOCATION_MAX_LENGTH} characters or fewer.`;
    }
    if (!['city', 'country'].includes(target.location_scope)) {
      return `Target ${index + 1} has an invalid scope.`;
    }
    if (!config.protestReportRegions.includes(target.region)) {
      return `Target ${index + 1} has an invalid region.`;
    }
    const key = `${target.location_scope}|${name.toLocaleLowerCase()}|${target.region}`;
    if (seen.has(key)) return `Target ${index + 1} duplicates another target.`;
    seen.add(key);
  }
  return '';
}

export function validateProtestRecipients(recipients) {
  if (!Array.isArray(recipients) || recipients.length < 1) {
    return 'Add at least one email recipient.';
  }
  if (recipients.length > PROTEST_RECIPIENT_MAX_COUNT) {
    return `A maximum of ${PROTEST_RECIPIENT_MAX_COUNT} recipients is allowed.`;
  }

  const seen = new Set();
  for (let index = 0; index < recipients.length; index += 1) {
    const email = (recipients[index] || '').trim();
    if (!email) return `Recipient ${index + 1} needs an email address.`;
    if (email.length > 254) {
      return `Recipient ${index + 1} must be 254 characters or fewer.`;
    }
    if (!EMAIL_PATTERN.test(email)) {
      return `Recipient ${index + 1} is not a valid email address.`;
    }
    const key = email.toLocaleLowerCase();
    if (seen.has(key)) return `Recipient ${index + 1} duplicates another address.`;
    seen.add(key);
  }
  return '';
}

function normalizeTarget(target) {
  return {
    location_scope: target.location_scope || 'city',
    name: target.name || '',
    region: target.region || config.protestReportRegions[0] || 'US',
  };
}

function formatSchedule(schedule) {
  if (!schedule) return 'Daily';
  if (typeof schedule === 'string') return schedule;
  return schedule.description || schedule.cron || JSON.stringify(schedule);
}

function ProtestMonitorConfigModal({
  isOpen,
  onClose,
  onSave,
  data,
  isLoading,
  isSaving,
  error,
  success,
}) {
  const [targets, setTargets] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTargets((data?.targets || []).map(normalizeTarget));
    setRecipients((data?.recipients || []).map((email) => email || ''));
    setValidationError('');
  }, [isOpen, data]);

  const regions = useMemo(
    () => config.protestReportRegions.length ? config.protestReportRegions : ['US'],
    []
  );

  if (!isOpen) return null;

  const disabled = isLoading || isSaving;
  const accessDenied = error?.code === 'ACCESS_DENIED' || error?.status === 403;

  const updateTarget = (index, field, value) => {
    setTargets((current) => current.map(
      (target, targetIndex) => targetIndex === index ? { ...target, [field]: value } : target
    ));
    setValidationError('');
  };

  const updateRecipient = (index, value) => {
    setRecipients((current) => current.map(
      (email, recipientIndex) => recipientIndex === index ? value : email
    ));
    setValidationError('');
  };

  const handleSave = (event) => {
    event.preventDefault();
    const targetValidation = validateProtestTargets(targets);
    if (targetValidation) {
      setValidationError(targetValidation);
      return;
    }
    const recipientValidation = validateProtestRecipients(recipients);
    if (recipientValidation) {
      setValidationError(recipientValidation);
      return;
    }
    onSave({
      targets: targets.map((target) => ({
        name: target.name.trim(),
        region: target.region,
        location_scope: target.location_scope,
      })),
      recipients: recipients.map((email) => email.trim()),
    });
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !isSaving) onClose();
  };

  return (
    <div className="protest-modal-overlay" onClick={handleOverlayClick}>
      <div className="protest-modal protest-monitor-modal" role="dialog" aria-modal="true" aria-labelledby="protest-monitor-title">
        <div className="protest-modal-header">
          <div className="protest-modal-title">
            <Icons.Protest />
            <h2 id="protest-monitor-title">Daily Protest Monitor</h2>
          </div>
          <button className="protest-modal-close" onClick={onClose} disabled={isSaving} type="button" aria-label="Close">
            <Icons.Close />
          </button>
        </div>

        <form className="protest-modal-content protest-monitor-content" onSubmit={handleSave}>
          {isLoading && <div className="protest-monitor-state">Loading effective global configuration…</div>}

          {!isLoading && accessDenied && (
            <div className="protest-error" role="alert">
              Access denied. You are not authorized to view or change this global configuration.
            </div>
          )}

          {!isLoading && !accessDenied && data && (
            <>
              <div className="protest-monitor-meta">
                <span><strong>Source:</strong> {data.source || 'global configuration'}</span>
                <span><strong>Schedule:</strong> {formatSchedule(data.schedule)}</span>
                <span><strong>Version:</strong> {data.version ?? '—'}</span>
              </div>

              {data.scan_active && (
                <div className="protest-monitor-notice" role="status">
                  A scan is currently active. Saving may be blocked until it completes.
                </div>
              )}

              <section className="protest-monitor-section">
                <h3 className="protest-monitor-section-title">Email recipients</h3>
                <p className="protest-monitor-section-help">
                  Daily digest emails are sent to every address in this list.
                </p>
                <div className="protest-monitor-recipients">
                  {recipients.map((email, index) => (
                    <div className="protest-monitor-recipient-row" key={`recipient-${index}`}>
                      <span className="protest-monitor-index" aria-hidden="true">{index + 1}</span>
                      <input
                        aria-label={`Recipient ${index + 1} email`}
                        type="email"
                        value={email}
                        onChange={(event) => updateRecipient(index, event.target.value)}
                        placeholder="name@example.com"
                        disabled={disabled}
                      />
                      <div className="protest-monitor-row-actions">
                        <button type="button" onClick={() => setRecipients((current) => moveProtestRecipient(current, index, -1))} disabled={disabled || index === 0} aria-label={`Move recipient ${index + 1} up`}>↑</button>
                        <button type="button" onClick={() => setRecipients((current) => moveProtestRecipient(current, index, 1))} disabled={disabled || index === recipients.length - 1} aria-label={`Move recipient ${index + 1} down`}>↓</button>
                        <button type="button" className="danger" onClick={() => setRecipients((current) => current.filter((_, recipientIndex) => recipientIndex !== index))} disabled={disabled || recipients.length <= 1} aria-label={`Remove recipient ${index + 1}`}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="protest-monitor-add"
                  onClick={() => setRecipients((current) => [...current, ''])}
                  disabled={disabled || recipients.length >= PROTEST_RECIPIENT_MAX_COUNT}
                >
                  + Add recipient
                </button>
              </section>

              <section className="protest-monitor-section">
                <h3 className="protest-monitor-section-title">Scan targets</h3>
                <div className="protest-monitor-targets">
                  {targets.map((target, index) => (
                    <div className="protest-monitor-row" key={`${index}-${target.location_scope}-${target.region}`}>
                      <span className="protest-monitor-index" aria-hidden="true">{index + 1}</span>
                      <select
                        aria-label={`Target ${index + 1} scope`}
                        value={target.location_scope}
                        onChange={(event) => updateTarget(index, 'location_scope', event.target.value)}
                        disabled={disabled}
                      >
                        <option value="city">City</option>
                        <option value="country">Country</option>
                      </select>
                      <input
                        aria-label={`Target ${index + 1} location`}
                        type="text"
                        value={target.name}
                        onChange={(event) => updateTarget(index, 'name', event.target.value)}
                        maxLength={PROTEST_TARGET_LOCATION_MAX_LENGTH}
                        placeholder={target.location_scope === 'country' ? 'Country name' : 'City name'}
                        disabled={disabled}
                      />
                      <select
                        aria-label={`Target ${index + 1} region`}
                        value={target.region}
                        onChange={(event) => updateTarget(index, 'region', event.target.value)}
                        disabled={disabled}
                      >
                        {regions.map((region) => <option key={region} value={region}>{region}</option>)}
                      </select>
                      <div className="protest-monitor-row-actions">
                        <button type="button" onClick={() => setTargets((current) => moveProtestTarget(current, index, -1))} disabled={disabled || index === 0} aria-label={`Move target ${index + 1} up`}>↑</button>
                        <button type="button" onClick={() => setTargets((current) => moveProtestTarget(current, index, 1))} disabled={disabled || index === targets.length - 1} aria-label={`Move target ${index + 1} down`}>↓</button>
                        <button type="button" className="danger" onClick={() => setTargets((current) => current.filter((_, targetIndex) => targetIndex !== index))} disabled={disabled} aria-label={`Remove target ${index + 1}`}>×</button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="protest-monitor-add"
                  onClick={() => setTargets((current) => [...current, emptyTarget()])}
                  disabled={disabled || targets.length >= 50}
                >
                  + Add target
                </button>
              </section>

              <p className="protest-monitor-help">
                Saving replaces the full global target and recipient lists. Changes apply to the next daily run, not a scan already in progress.
              </p>
            </>
          )}

          {!isLoading && !accessDenied && error && (
            <div className="protest-error" role="alert">{error.message || String(error)}</div>
          )}
          {validationError && <div className="protest-error" role="alert">{validationError}</div>}
          {success && <div className="protest-monitor-success" role="status">{success}</div>}

          <div className="protest-modal-actions">
            <button type="button" className="protest-btn-cancel" onClick={onClose} disabled={isSaving}>Close</button>
            {!accessDenied && data && (
              <button type="submit" className="protest-btn-submit" disabled={disabled}>
                {isSaving ? <><span className="protest-loading-spinner" />Saving…</> : 'Save configuration'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProtestMonitorConfigModal;
